import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import "./dashboard.css";
import "./teacherEdit.css";
import AsyncStorage from "./utils/asyncStorage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faPhone,
  faAddressCard,
  faGraduationCap,
  faSchool,
  faSpinner,
  faArrowLeft,
  faTrash,
  faSave,
  faIdCard,
  faHome,
  faBriefcase,
  faCheckCircle,
  faDatabase,
  faCloudDownload,
  faExclamationTriangle,
  faEnvelope,
  faMars,
  faVenus,
  faTransgender,
  faSearch,
  faSync,
} from "@fortawesome/free-solid-svg-icons";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api/TeacherCRUD.php";

const EditTeacherForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [dataSource, setDataSource] = useState("");
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Get teacher ID from navigation state
  const teacherId = location.state?.id;

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    fatherName: "",
    cnic: "",
    primaryMobile: "",
    gmail: "",
    gender: "",
    address: "",
    highestEducation: "",
    gradeOrCgpa: "",
    selectFor: "",
    inchargeOf: "",
    teacherId: "",
    status: "Active",
  });

  const educationLevels = [
    "Matric",
    "Intermediate",
    "Bachelor",
    "Master",
    "BS-16 Years",
    "M-Phil",
    "PhD",
  ];

  const subjectsFor = ["Science Subjects", "Arts Subjects", "Hybrid"];
  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];
  const genderOptions = ["Male", "Female", "Other"];

  // Auto-format mobile numbers as user types - EXACTLY as backend expects
  const formatMobile = (value) => {
    if (!value) return "";
    
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, "");
    
    // Format: 03XX-XXXXXXX (EXACTLY as backend expects)
    if (numbers.length <= 4) {
      return numbers;
    }
    
    const part1 = numbers.slice(0, 4);
    const part2 = numbers.slice(4, 11);
    
    if (part2) {
      return `${part1}-${part2}`;
    }
    
    return part1;
  };

  // Auto-format CNIC as user types - EXACTLY as backend expects
  const formatCNIC = (value) => {
    if (!value) return "";
    
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, "");
    
    // Format: XXXXX-XXXXXXX-X (EXACTLY as backend expects)
    if (numbers.length <= 5) {
      return numbers;
    } else if (numbers.length <= 12) {
      return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    } else {
      return `${numbers.slice(0, 5)}-${numbers.slice(5, 12)}-${numbers.slice(12, 13)}`;
    }
  };

  // Format raw data from server
  const formatServerData = (data) => {
    const formattedData = { ...data };
    
    // Auto-format mobile number from server data
    if (formattedData.primaryMobile || formattedData.primary_mobile) {
      const mobile = formattedData.primaryMobile || formattedData.primary_mobile;
      const mobileStr = mobile.toString();
      formattedData.primaryMobile = formatMobile(mobileStr);
    }
    
    // Auto-format CNIC from server data
    if (formattedData.cnic) {
      const cnicStr = formattedData.cnic.toString();
      formattedData.cnic = formatCNIC(cnicStr);
    }
    
    return formattedData;
  };

  // Load teacher data on component mount
  useEffect(() => {
    const loadTeacherData = async () => {
      setLoading(true);
      setError("");
      setFormErrors({});

      if (teacherId) {
        console.log("Teacher ID from navigation:", teacherId);
        await fetchTeacherById(teacherId);
      } else {
        await searchTeacherFromAsyncStorage();
      }
    };

    loadTeacherData();
  }, [teacherId]);

  // Search teacher from AsyncStorage criteria
  const searchTeacherFromAsyncStorage = async () => {
    setSearching(true);
    setError("");
    setFormErrors({});

    try {
      const primaryMobile = await AsyncStorage.getItem("primaryMobile");
      const gmail = await AsyncStorage.getItem("gmail");
      const cnic = await AsyncStorage.getItem("cnic");

      if (!primaryMobile && !gmail && !cnic) {
        setLoading(false);
        setSearching(false);
        setError("No search criteria found in AsyncStorage.");

        Swal.fire({
          icon: "info",
          title: "No Search Criteria",
          html: `
            <p>No teacher details found in AsyncStorage.</p>
            <p>Please either:</p>
            <ul style="text-align: left; margin: 10px 0;">
              <li>Select a teacher from the list, or</li>
              <li>Make sure you have teacher details in AsyncStorage</li>
            </ul>
          `,
          confirmButtonColor: "#667eea",
        }).then(() => {
          navigate("/fetch-teachers");
        });
        return;
      }

      await searchTeacherByCriteria(primaryMobile, gmail, cnic);
    } catch (error) {
      console.error("Error searching from AsyncStorage:", error);
      setLoading(false);
      setSearching(false);
      setError("Failed to search teacher. Please try again.");
    }
  };

  // Search teacher by criteria
  const searchTeacherByCriteria = async (primaryMobile, gmail, cnic) => {
    try {
      const params = {
        action: "fetch-teachers",
        limit: 10,
      };

      if (primaryMobile) {
        params.useTeacherMobile = true;
        params.teacher_mobile = primaryMobile.replace(/-/g, '');
      }

      if (cnic) {
        params.useTeacherCNIC = true;
        params.teacher_cnic = cnic.replace(/-/g, '');
      }

      const response = await axios.get(API_BASE_URL, { params });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        let foundTeacher = null;

        if (primaryMobile) {
          const searchMobile = primaryMobile.replace(/-/g, '');
          foundTeacher = response.data.data.find(
            (teacher) => teacher.primary_mobile && 
              teacher.primary_mobile.replace(/-/g, '') === searchMobile
          );
        }

        if (!foundTeacher && gmail) {
          foundTeacher = response.data.data.find(
            (teacher) =>
              teacher.gmail &&
              teacher.gmail.toLowerCase() === gmail.toLowerCase()
          );
        }

        if (!foundTeacher && cnic) {
          const searchCNIC = cnic.replace(/-/g, '');
          foundTeacher = response.data.data.find(
            (teacher) => teacher.cnic && 
              teacher.cnic.replace(/-/g, '') === searchCNIC
          );
        }

        if (!foundTeacher && response.data.data.length > 0) {
          foundTeacher = response.data.data[0];
        }

        if (foundTeacher) {
          await fetchTeacherById(foundTeacher.id);
        } else {
          throw new Error("Teacher not found with the given criteria");
        }
      } else {
        throw new Error("No teachers found with the given criteria");
      }
    } catch (error) {
      console.error("Search error:", error);
      setLoading(false);
      setSearching(false);

      let errorMessage = "Teacher not found. Please check your search criteria.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);

      Swal.fire({
        icon: "error",
        title: "Teacher Not Found",
        text: errorMessage,
        confirmButtonColor: "#667eea",
      }).then(() => {
        navigate("/fetch-teachers");
      });
    }
  };

  // Fetch teacher by ID from server
  const fetchTeacherById = async (id) => {
    try {
      const response = await axios.get(API_BASE_URL, {
        params: {
          action: "get-teacher-details",
          id: id,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to fetch teacher data");
      }

      const teacherData = response.data.data;

      const processedData = {
        id: teacherData.id || "",
        name: teacherData.name || "",
        fatherName: teacherData.fatherName || teacherData.father_name || "",
        cnic: teacherData.cnic || "",
        primaryMobile: teacherData.primaryMobile || teacherData.primary_mobile || "",
        gmail: teacherData.gmail || "",
        gender: teacherData.gender || "",
        address: teacherData.address || "",
        highestEducation: teacherData.highestEducation || teacherData.highest_education || "",
        gradeOrCgpa: teacherData.gradeOrCgpa || teacherData.grade_or_cgpa || "",
        selectFor: teacherData.selectFor || teacherData.select_for || "",
        inchargeOf: teacherData.inchargeOf || teacherData.incharge_of || "",
        teacherId: teacherData.teacherId || teacherData.teacher_id || "",
      };

      const formattedData = formatServerData(processedData);
      
      setFormData(formattedData);
      setDataSource("server");
      setError("");
      setFormErrors({});

      await AsyncStorage.setItem("currentEditingTeacher", JSON.stringify(formattedData));

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.error("Error fetching teacher data:", error);

      let errorMessage = "Failed to fetch teacher data. Please try again.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setLoading(false);
      setSearching(false);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#667eea",
      }).then(() => {
        navigate("/fetch-teachers");
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;

    // Auto-format CNIC as user types
    if (name === "cnic") {
      formattedValue = formatCNIC(value);
    } 
    // Auto-format mobile number as user types
    else if (name === "primaryMobile") {
      formattedValue = formatMobile(value);
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Basic required field validation only
    if (!formData.name.trim()) {
      errors.name = "Teacher name is required";
      isValid = false;
    }
    if (!formData.fatherName.trim()) {
      errors.fatherName = "Father's name is required";
      isValid = false;
    }
    
    // CNIC validation - ONLY CHECK IF NOT EMPTY
    if (!formData.cnic.trim()) {
      errors.cnic = "CNIC is required";
      isValid = false;
    }
    
    // Mobile validation - ONLY CHECK IF NOT EMPTY
    if (!formData.primaryMobile.trim()) {
      errors.primaryMobile = "Mobile number is required";
      isValid = false;
    }
    
    // Email validation
    if (!formData.gmail) {
      errors.gmail = "Gmail address is required";
      isValid = false;
    } else if (!formData.gmail.includes("@gmail.com")) {
      errors.gmail = "Must be a valid Gmail address (@gmail.com)";
      isValid = false;
    }

    if (!formData.gender) {
      errors.gender = "Gender is required";
      isValid = false;
    }
    if (!formData.address.trim()) {
      errors.address = "Address is required";
      isValid = false;
    }
    if (!formData.highestEducation) {
      errors.highestEducation = "Highest education is required";
      isValid = false;
    }
    if (!formData.selectFor) {
      errors.selectFor = "Subject specialization is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        html: `
          <div style="text-align: left; font-size: 14px;">
            <p>Please fill in all required fields:</p>
            <ul style="margin-left: 20px;">
              ${Object.values(formErrors).filter(err => err).map(err => `<li>${err}</li>`).join('')}
            </ul>
          </div>
        `,
        confirmButtonColor: "#667eea",
      });
      return;
    }

    console.log("Form Data to submit:", formData);

    try {
      setLoading(true);

      // IMPORTANT: Send data WITH dashes as backend expects
      const submitData = new URLSearchParams();
      submitData.append("action", "update-teacher");
      submitData.append("id", formData.id);
      submitData.append("name", formData.name);
      submitData.append("fatherName", formData.fatherName);
      submitData.append("cnic", formData.cnic); // WITH dashes
      submitData.append("primaryMobile", formData.primaryMobile); // WITH dashes
      submitData.append("gmail", formData.gmail);
      submitData.append("gender", formData.gender);
      submitData.append("address", formData.address);
      submitData.append("highestEducation", formData.highestEducation);
      submitData.append("gradeOrCgpa", formData.gradeOrCgpa || "");
      submitData.append("selectFor", formData.selectFor);
      submitData.append("inchargeOf", formData.inchargeOf || "");
      submitData.append("teacherId", formData.teacherId);

      console.log("Sending update data (with dashes):", Object.fromEntries(submitData.entries()));

      const response = await axios.post(API_BASE_URL, submitData.toString(), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      console.log("Update response:", response.data);

      if (response.data.success) {
        setLoading(false);
        setSuccessMessage("Teacher information updated successfully!");

        await AsyncStorage.setItem("currentEditingTeacher", JSON.stringify(formData));

        setTimeout(() => setSuccessMessage(""), 3000);

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message || "Teacher updated successfully",
          confirmButtonColor: "#667eea",
          timer: 2000,
        });
      } else {
        let errorMessage = response.data.error || "Update failed";
        
        Swal.fire({
          icon: "warning",
          title: "Server Validation Error",
          html: `
            <div style="text-align: left; font-size: 14px;">
              <p>The server returned an error:</p>
              <p><strong>${errorMessage}</strong></p>
              <p style="margin-top: 10px; color: #718096; font-size: 12px;">
                Please check if CNIC is in format: XXXXX-XXXXXXX-X (13 digits)<br>
                Mobile should be: 03XX-XXXXXXX (11 digits)
              </p>
            </div>
          `,
          confirmButtonColor: "#e53e3e",
        });
      }
    } catch (error) {
      setLoading(false);
      console.error("Submission Error:", error);

      let errorMessage = "There was an issue updating the teacher information. Please try again.";

      if (error.response) {
        errorMessage = error.response.data?.error || errorMessage;
        if (error.response.data?.details) {
          errorMessage += `\n${error.response.data.details}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        html: `
          <div style="text-align: left; font-size: 14px; white-space: pre-wrap;">
            <p>${errorMessage}</p>
          </div>
        `,
        confirmButtonColor: "#e53e3e",
      });
    }
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      html: `
        <div style="text-align: center; padding: 1rem;">
          <div style="font-size: 4rem; color: #f56565; margin-bottom: 1rem;">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <h3 style="color: #2d3748; margin-bottom: 0.5rem;">Delete Teacher?</h3>
          <p style="color: #718096;">
            Are you sure you want to delete <strong>${formData.name}</strong>?
            This action cannot be undone.
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#a0aec0",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      background: "#f8fafc",
      color: "#2d3748",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const deleteData = new URLSearchParams();
          deleteData.append("action", "delete-teacher");
          deleteData.append("id", formData.id);

          const response = await axios.post(
            API_BASE_URL,
            deleteData.toString(),
            {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
            }
          );

          if (response.data.success) {
            await AsyncStorage.removeItem("currentEditingTeacher");
            await AsyncStorage.removeItem("primaryMobile");
            await AsyncStorage.removeItem("gmail");
            await AsyncStorage.removeItem("cnic");

            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Teacher has been deleted successfully.",
              confirmButtonColor: "#667eea",
            }).then(() => {
              navigate("/fetch-teachers");
            });
          } else {
            throw new Error(response.data.error || "Delete failed");
          }
        } catch (error) {
          console.error("Delete error:", error);

          let errorMessage = "Failed to delete teacher record.";
          if (error.response) {
            errorMessage = error.response.data?.error || errorMessage;
          }

          Swal.fire({
            icon: "error",
            title: "Error!",
            text: errorMessage,
            confirmButtonColor: "#e53e3e",
          });
        }
      }
    });
  };

  const handleCancel = () => {
    AsyncStorage.removeItem("currentEditingTeacher");
    navigate("/fetch-teachers");
  };

  const handleRefreshFromServer = async () => {
    if (!formData.id) {
      await searchTeacherFromAsyncStorage();
      return;
    }

    try {
      setLoading(true);
      setError("");

      await fetchTeacherById(formData.id);

      Swal.fire({
        icon: "success",
        title: "Data Refreshed",
        text: "Teacher data refreshed from server.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      setLoading(false);
      setSearching(false);

      let errorMessage = "Failed to refresh data from server.";
      if (error.response) {
        errorMessage = error.response.data?.error || errorMessage;
      }

      Swal.fire({
        icon: "error",
        title: "Refresh Failed",
        text: errorMessage,
        confirmButtonColor: "#e53e3e",
      });
    }
  };

  const handleManualSearch = async () => {
    await searchTeacherFromAsyncStorage();
  };

  if (loading || searching) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span style={{ marginLeft: "1rem", color: "#4a5568" }}>
            {searching ? "Searching for teacher..." : "Loading teacher data..."}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="teacher-edit-container" style={{marginTop:'5vh'}}>
        {/* Header */}
        <div className="form-header">
          <div>
            <h2>
              <FontAwesomeIcon icon={faUser} />
              Edit Teacher Information
            </h2>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginTop: "0.5rem",
              }}
            >
              <p style={{ color: "#718096", fontSize: "0.95rem" }}>
                {formData.id
                  ? `Update details for ${formData.name}`
                  : "Search for teacher using AsyncStorage"}
              </p>
              {dataSource && (
                <span
                  className="stats-badge"
                  style={{
                    background:
                      "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
                    fontSize: "0.75rem",
                    padding: "0.25rem 0.75rem",
                  }}
                >
                  <FontAwesomeIcon icon={faCloudDownload} />
                  {`Data from ${dataSource}`}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {!formData.id && (
              <button
                onClick={handleManualSearch}
                className="btn btn-primary"
                style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
                title="Search teacher from AsyncStorage"
                disabled={searching}
              >
                <FontAwesomeIcon icon={faSearch} />
                {searching ? "Searching..." : "Search from Storage"}
              </button>
            )}
            <button
              onClick={handleRefreshFromServer}
              className="btn btn-secondary"
              style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
              title="Refresh data from server"
              disabled={loading || searching}
            >
              <FontAwesomeIcon icon={faSync} spin={loading || searching} />
              Refresh
            </button>
            {formData.id && (
              <div className="teacher-status-badge">
                <FontAwesomeIcon icon={faCheckCircle} />
                Active Teacher
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && !formData.id && (
          <div
            className="error-message"
            style={{
              background: "#fed7d7",
              border: "1px solid #fc8181",
              color: "#c53030",
              padding: "1rem",
              borderRadius: "8px",
              marginBottom: "1rem",
            }}
          >
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              style={{ marginRight: "0.5rem" }}
            />
            {error}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="success-message">
            <FontAwesomeIcon icon={faCheckCircle} />
            {successMessage}
          </div>
        )}

        {/* Teacher Info Card and Edit Form (when teacher loaded) */}
        {formData.id ? (
          <>
            <div className="teacher-info-card">
              <div className="teacher-info-grid">
                <div className="info-item">
                  <span className="info-label">
                    <FontAwesomeIcon icon={faIdCard} /> Teacher ID
                  </span>
                  <div className="teacher-id-display">
                    {formData.teacherId || "Not Assigned"}
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <FontAwesomeIcon icon={faUser} /> Name
                  </span>
                  <span className="info-value">{formData.name || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <FontAwesomeIcon icon={faEnvelope} /> Email
                  </span>
                  <span className="info-value">{formData.gmail || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    {formData.gender === "Male" ? (
                      <FontAwesomeIcon icon={faMars} />
                    ) : formData.gender === "Female" ? (
                      <FontAwesomeIcon icon={faVenus} />
                    ) : (
                      <FontAwesomeIcon icon={faTransgender} />
                    )}{" "}
                    Gender
                  </span>
                  <span className="info-value">{formData.gender || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Edit Form */}
            <div className="edit-form-card">
              <h3>
                <FontAwesomeIcon icon={faUser} />
                Update Teacher Details
              </h3>

              <form onSubmit={handleSubmit}>
                <div className="form-section">
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faUser} />
                    Personal Information
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faUser} /> Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleInputChange}
                        className={`form-input ${formErrors.name ? 'input-error' : ''}`}
                        required
                        placeholder="Enter full name"
                      />
                      {formErrors.name && (
                        <small className="error-text">{formErrors.name}</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faUser} /> Father's Name *
                      </label>
                      <input
                        type="text"
                        name="fatherName"
                        value={formData.fatherName || ""}
                        onChange={handleInputChange}
                        className={`form-input ${formErrors.fatherName ? 'input-error' : ''}`}
                        required
                        placeholder="Enter father's name"
                      />
                      {formErrors.fatherName && (
                        <small className="error-text">{formErrors.fatherName}</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faAddressCard} /> CNIC Number *
                      </label>
                      <input
                        type="text"
                        name="cnic"
                        value={formData.cnic || ""}
                        onChange={handleInputChange}
                        className={`form-input ${formErrors.cnic ? 'input-error' : ''}`}
                        required
                        placeholder="35202-1234567-1"
                        maxLength="15"
                      />
                      <small className="format-note">
                        System auto-formats: XXXXX-XXXXXXX-X (13 digits)
                      </small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faPhone} /> Primary Mobile *
                      </label>
                      <input
                        type="text"
                        name="primaryMobile"
                        value={formData.primaryMobile || ""}
                        onChange={handleInputChange}
                        className={`form-input ${formErrors.primaryMobile ? 'input-error' : ''}`}
                        required
                        placeholder="0300-1234567"
                        maxLength="12"
                      />
                      <small className="format-note">
                        System auto-formats: 03XX-XXXXXXX (11 digits)
                      </small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faEnvelope} /> Gmail Address *
                      </label>
                      <input
                        type="email"
                        name="gmail"
                        value={formData.gmail || ""}
                        onChange={handleInputChange}
                        className={`form-input ${formErrors.gmail ? 'input-error' : ''}`}
                        required
                        placeholder="username@gmail.com"
                      />
                      {formErrors.gmail && (
                        <small className="error-text">{formErrors.gmail}</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {formData.gender === "Male" ? (
                          <FontAwesomeIcon icon={faMars} />
                        ) : formData.gender === "Female" ? (
                          <FontAwesomeIcon icon={faVenus} />
                        ) : (
                          <FontAwesomeIcon icon={faTransgender} />
                        )}{" "}
                        Gender *
                      </label>
                      <select
                        name="gender"
                        value={formData.gender || ""}
                        onChange={handleInputChange}
                        className={`form-select ${formErrors.gender ? 'input-error' : ''}`}
                        required
                      >
                        <option value="">Select Gender</option>
                        {genderOptions.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {formErrors.gender && (
                        <small className="error-text">{formErrors.gender}</small>
                      )}
                    </div>

                    <div
                      className="form-group"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <label className="form-label">
                        <FontAwesomeIcon icon={faHome} /> Address *
                      </label>
                      <textarea
                        name="address"
                        value={formData.address || ""}
                        onChange={handleInputChange}
                        className={`form-textarea ${formErrors.address ? 'input-error' : ''}`}
                        required
                        placeholder="Enter complete residential address..."
                        rows="3"
                      ></textarea>
                      {formErrors.address && (
                        <small className="error-text">{formErrors.address}</small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faGraduationCap} />
                    Education & Assignment
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faGraduationCap} /> Highest
                        Education *
                      </label>
                      <select
                        name="highestEducation"
                        value={formData.highestEducation || ""}
                        onChange={handleInputChange}
                        className={`form-select ${formErrors.highestEducation ? 'input-error' : ''}`}
                        required
                      >
                        <option value="">Select Education Level</option>
                        {educationLevels.map((level, index) => (
                          <option key={index} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                      {formErrors.highestEducation && (
                        <small className="error-text">{formErrors.highestEducation}</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faSchool} /> Grade / CGPA
                        (Optional)
                      </label>
                      <input
                        type="text"
                        name="gradeOrCgpa"
                        value={formData.gradeOrCgpa || ""}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="e.g., 3.36, A+, First Division"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faBriefcase} /> Subject Category
                        *
                      </label>
                      <select
                        name="selectFor"
                        value={formData.selectFor || ""}
                        onChange={handleInputChange}
                        className={`form-select ${formErrors.selectFor ? 'input-error' : ''}`}
                        required
                      >
                        <option value="">Select Subject Type</option>
                        {subjectsFor.map((subject, index) => (
                          <option key={index} value={subject}>
                            {subject}
                          </option>
                        ))}
                      </select>
                      {formErrors.selectFor && (
                        <small className="error-text">{formErrors.selectFor}</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faSchool} /> Incharge Of Class
                        (Optional)
                      </label>
                      <select
                        name="inchargeOf"
                        value={formData.inchargeOf || ""}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select Class (Optional)</option>
                        {classes.map((cls, index) => (
                          <option key={index} value={cls}>
                            {cls}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div
                      className="form-group"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <label className="form-label">
                        <FontAwesomeIcon icon={faIdCard} /> Teacher ID
                      </label>
                      <input
                        type="text"
                        name="teacherId"
                        value={formData.teacherId || "Will be auto-generated"}
                        readOnly
                        className="form-input readonly"
                      />
                      <small
                        style={{
                          color: "#718096",
                          fontSize: "0.875rem",
                          marginTop: "0.5rem",
                        }}
                      >
                        This ID is auto-generated and cannot be modified
                      </small>
                    </div>
                  </div>
                </div>

                {/* Hidden ID field */}
                <input type="hidden" name="id" value={formData.id} />

                {/* Form Actions */}
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Delete Teacher
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} />
                        Update Teacher
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          // No teacher loaded - Show search prompt
          <div
            className="search-panel"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              padding: "2rem",
              marginBottom: "2rem",
              color: "white",
              textAlign: "center",
            }}
          >
            <h3
              style={{
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <FontAwesomeIcon icon={faSearch} />
              Search Teacher from AsyncStorage
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
              Click the button below to search for a teacher using the details
              stored in AsyncStorage.
            </p>
            <button
              onClick={handleManualSearch}
              className="btn btn-primary"
              style={{
                background: "white",
                color: "#667eea",
                fontWeight: "bold",
                padding: "0.75rem 2rem",
              }}
              disabled={searching}
            >
              <FontAwesomeIcon
                icon={faSearch}
                style={{ marginRight: "0.5rem" }}
              />
              {searching ? "Searching..." : "Search Teacher Now"}
            </button>
          </div>
        )}

        {/* Footer Note */}
        <div
          style={{
            textAlign: "center",
            color: "#a0aec0",
            fontSize: "0.875rem",
            marginTop: "2rem",
            padding: "1rem",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          Last updated: {new Date().toLocaleDateString()} | All changes are
          logged in the system
        </div>
      </div>
    </div>
  );
};

export default EditTeacherForm;